import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { Err, Ok } from 'ts-results'

import { useNotifications } from 'app/application/application'
import { useConnections } from 'app/connections/application'
import { ConnectionConfig, SupportedAccount, isInstalledConnection } from 'app/connections/types'

import { Button } from 'components'

import { useConnection } from './Connection'
import GenericSettings from './GenericSettings'
import InnkeeprSettings from './InnkeeprSettings/InnkeeprSettings'
import styles from './Setup.module.scss'
import ShopifySettings from './ShopifySettings'

export type AuthorizationData = {
  authorizationCode: string | undefined
  redirectUri: string
}

export interface SettingsProps {
  onConnectionConfigValidationUpdate: (isValidConfig: boolean) => void
}

// eslint-disable-next-line react-refresh/only-export-components
export const noSaveIfInstalled = ['innkeepr']

const ConnectionSettings = () => {
  const navigate = useNavigate()
  const connection = useConnection()
  const { t } = useTranslation('connections')

  const { createNotification, te } = useNotifications()
  const { saveConnection } = useConnections()

  const [connectionConfig, setConnectionConfig] = useState<ConnectionConfig>(connection.config)
  const [authorizationData, setAuthorizationData] = useState<AuthorizationData>({
    authorizationCode: undefined,
    redirectUri: `${window.location.origin}/connections/${connection.name.toLowerCase()}`,
  })
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isValidConfig, setIsValidConfig] = useState<boolean>(false)

  const handleSave = useCallback(
    async (authorizationData?: AuthorizationData) => {
      setIsSaving(true)

      return saveConnection(
        connection,
        connectionConfig,
        authorizationData?.authorizationCode,
        authorizationData?.redirectUri,
      )
        .then((result) => {
          if (result.ok) {
            createNotification({
              message: t(
                `${isInstalledConnection(connection) ? 'update' : 'create'}.success.message`,
                {
                  name: t(`available.${connection.name}.displayName`),
                },
              ),
              title: t(`${isInstalledConnection(connection) ? 'update' : 'create'}.success.title`),
              type: 'success',
            })

            setAuthorizationData((prev) => ({ ...prev, authorizationCode: undefined }))
            return Ok(undefined)
          } else {
            createNotification({
              message: result.val.message,
              title: te(`http.${result.val.code}`),
              type: 'error',
            })

            return Err(authorizationData ? result.val.message : undefined)
          }
        })
        .finally(() => setIsSaving(false))
    },
    [createNotification, saveConnection, connection, connectionConfig, t, te],
  )

  const showSaveButton = () => {
    if (!connection) return false
    if (connection.readonly) return false

    if (isInstalledConnection(connection) && noSaveIfInstalled.includes(connection.name)) {
      return false
    }

    return true
  }

  const handleConnectionConfigUpdate = useCallback(
    (key: string, value: SupportedAccount[] | string) =>
      setConnectionConfig((prev) =>
        prev ? ({ ...prev, [key]: value } as ConnectionConfig) : prev,
      ),
    [],
  )

  const handleOnTrackingUrlUpdate = useCallback(
    (trackingUrl: string) => handleConnectionConfigUpdate('trackingUrl', trackingUrl),
    [handleConnectionConfigUpdate],
  )

  const renderConnectionSettings = () => {
    switch (connection.name) {
      case 'innkeepr':
        return (
          <InnkeeprSettings
            isSaving={isSaving}
            onConnectionConfigValidationUpdate={setIsValidConfig}
            onSave={handleSave}
            onTrackingUrlUpdate={handleOnTrackingUrlUpdate}
            connection={connection}
            trackingUrl={connection.config.trackingUrl}
          />
        )
      case 'shopify':
        return <ShopifySettings connection={connection} />
      default:
        return (
          <>
            <GenericSettings
              authorizationData={authorizationData}
              handleSaveAuthorization={handleSave}
              onConnectionConfigUpdate={handleConnectionConfigUpdate}
              onConnectionConfigValidationUpdate={setIsValidConfig}
              setAuthorizationData={setAuthorizationData}
              connection={connection}
              connectionConfig={connectionConfig}
            />
            <div className={styles.actions}>
              <Button onClick={() => void navigate(-1)} variant="secondary">
                {t('setup.actions.back')}
              </Button>
              {showSaveButton() && (
                <Button
                  disabled={!isValidConfig}
                  loading={isSaving}
                  onClick={(e) => {
                    e.stopPropagation()

                    void handleSave(authorizationData)
                  }}
                >
                  {t('setup.actions.save')}
                </Button>
              )}
            </div>
          </>
        )
    }
  }

  return renderConnectionSettings()
}

export default ConnectionSettings
